/**
 * NOVAPAY-116-RC3 — canonical LegalDocumentRegistry (single SoT for routes/versions).
 */
import { buildPublicOfferBody, PUBLIC_OFFER_VERSION } from "./legal-content-offer-rc3-v2.js";
import { buildPrivacyPolicyBody, PRIVACY_POLICY_VERSION } from "./legal-content-privacy-rc3-v2.js";
import { CONSENT_VERSION } from "./store-info-v2.js";
import { STORE_INFO } from "./store-merchant-sot-v2.js";

export type LegalDocumentId =
  | "about_store"
  | "public_offer"
  | "privacy_policy"
  | "payment_and_delivery"
  | "returns_and_refunds"
  | "merchant_details"
  | "contacts"
  | "data_management";

export type LegalHubPanel =
  | "about"
  | "offer"
  | "privacy"
  | "payment"
  | "returns"
  | "requisites"
  | "contacts"
  | "data";

export interface LegalDocumentRecord {
  document_id: LegalDocumentId;
  version: string;
  locale: "ua" | "en";
  effective_from: string;
  canonical_route: string;
  content_hash: string;
  publication_state: "published";
  approved_by: "owner";
  approved_at: string;
  previous_version?: string;
  hub_panel: LegalHubPanel;
}

export function hashLegalContent(parts: string[]): string {
  let h = 2166136261;
  const s = parts.join("\n");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `fnv1a-${(h >>> 0).toString(16)}`;
}

const EFFECTIVE = "2026-07-21";
const APPROVED_AT = "2026-07-21";

export const OFFER_VERSION = PUBLIC_OFFER_VERSION;
export const PRIVACY_VERSION = PRIVACY_POLICY_VERSION;

export const LEGAL_ACCEPTANCE_VERSION = CONSENT_VERSION;

function route(panel: LegalHubPanel): string {
  return `/staging-store-v2/?legal=${panel}&publicLegal=1`;
}

export function buildLegalDocumentRegistry(lang: "ua" | "en"): LegalDocumentRecord[] {
  const offerBody = buildPublicOfferBody(lang);
  const privacyBody = buildPrivacyPolicyBody(lang);
  const loc = lang;
  return [
    {
      document_id: "about_store",
      version: "about-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("about"),
      content_hash: hashLegalContent([STORE_INFO.name, STORE_INFO.slogan]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "about",
    },
    {
      document_id: "public_offer",
      version: OFFER_VERSION,
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("offer"),
      content_hash: hashLegalContent(offerBody),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      previous_version: "v4-2026-07-19",
      hub_panel: "offer",
    },
    {
      document_id: "privacy_policy",
      version: PRIVACY_VERSION,
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("privacy"),
      content_hash: hashLegalContent(privacyBody),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      previous_version: "v4-2026-07-19",
      hub_panel: "privacy",
    },
    {
      document_id: "payment_and_delivery",
      version: "payment-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("payment"),
      content_hash: hashLegalContent(["payment", loc]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "payment",
    },
    {
      document_id: "returns_and_refunds",
      version: "returns-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("returns"),
      content_hash: hashLegalContent(["returns", loc]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "returns",
    },
    {
      document_id: "merchant_details",
      version: "merchant-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("requisites"),
      content_hash: hashLegalContent([STORE_INFO.edrpou, STORE_INFO.iban]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "requisites",
    },
    {
      document_id: "contacts",
      version: "contacts-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("contacts"),
      content_hash: hashLegalContent([STORE_INFO.email, STORE_INFO.phone]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "contacts",
    },
    {
      document_id: "data_management",
      version: "data-v3-rc3-2026-07-21",
      locale: loc,
      effective_from: EFFECTIVE,
      canonical_route: route("data"),
      content_hash: hashLegalContent(["data_management", loc]),
      publication_state: "published",
      approved_by: "owner",
      approved_at: APPROVED_AT,
      hub_panel: "data",
    },
  ];
}

export function resolvePublicLegalPanel(raw: string | null | undefined): LegalHubPanel | null {
  const v = String(raw ?? "").trim();
  const allowed: LegalHubPanel[] = [
    "about",
    "offer",
    "privacy",
    "payment",
    "returns",
    "requisites",
    "contacts",
    "data",
  ];
  return (allowed as string[]).includes(v) ? (v as LegalHubPanel) : null;
}

export function stagingPublicLegalUrl(
  panel: LegalHubPanel,
  origin = "https://staging.asiastyle-mini-app.pages.dev",
): string {
  const base = origin.replace(/\/$/, "");
  return `${base}/staging-store-v2/?legal=${panel}&publicLegal=1`;
}
